/**
 * condoDocumentsCopy.js — every user-facing string in the condo documents panel.
 *
 * Legal wording comes verbatim from
 * docs/legal/states/WI/paperwork-copy-and-rules.v1.md §5 (task L3). Each legal
 * string names its stable key (`WI.condo_docs.*`) in a comment. If the wording
 * changes, the legal file bumps its version and the `*_VERSION` constants
 * below change with it. Strings without a key are UI chrome (buttons, labels,
 * status lines) and make no legal statement.
 *
 * The two attestation versions MUST match what the server expects: `deliver`
 * and `acknowledge` refuse a stale version with a 409. When the summary
 * response carries `attestations.{seller_delivery,buyer_receipt}.{version,text}`
 * the panel prefers those over the constants below.
 *
 * `{date}`-style values are formatted like "Friday, October 9, 2026"
 * (condoDocumentsFormat.formatLongLocalDate / formatLongTimestamp).
 */

/**
 * `{time_zone}` in the legal file. The clock sentences take the server's
 * `deadline.time_zone_label` as `timeZoneLabel`; this is the fallback.
 */
export const CONDO_TIME_ZONE_LABEL = 'Central time';

/** Checklist labels by § 703.33 item code (legal §5.2, `WI.condo_docs.item.*`). */
export const CONDO_ITEM_LABELS = {
  cover_sheet: 'Cover sheet with the statements required by Wis. Stat. § 703.33(2)',
  index: 'Index of the documents',
  declaration: 'Declaration',
  bylaws: 'Bylaws',
  rules: 'Rules and regulations',
  articles: 'Articles of incorporation of the association',
  management_and_other_contracts:
    'Management, employment and other contracts affecting the use, maintenance or access of the condominium',
  budget:
    "Projected annual operating budget, including estimated monthly assessments and charges for facilities that aren't part of the condominium",
  leases: 'Leases the unit owners or the association will be a party to after closing',
  expansion_plans: 'Description of any planned expansion of the condominium',
  floor_plan: 'Floor plan of the unit, showing the common elements and other facilities',
  executive_summary: 'Executive summary',
  condominium_plat: 'Condominium plat',
};

/**
 * Not-applicable note presets (legal §5.2, `WI.condo_docs.na.*`). The seller
 * may edit the note; a note is still required.
 */
export const CONDO_ITEM_NA_PRESETS = {
  rules: 'The association has not adopted any rules or regulations.',
  articles: 'The association is not incorporated.',
  management_and_other_contracts: 'There are no such contracts.',
  leases: 'There are no such leases.',
  expansion_plans: 'No expansion of the condominium is contemplated.',
  executive_summary: 'No executive summary has been prepared.',
};

/** `WI.condo_docs.na.budget_small_condo`: the budget preset for a small condominium. */
export const CONDO_NA_PRESET_BUDGET_SMALL_CONDO = 'Not applicable to this small condominium.';

/**
 * The small-condominium question (`WI.condo_docs.small_condo.*`). The seller
 * view renders the server's `summary.small_condominium_question`; this is the
 * fallback when a summary lacks it.
 */
export const CONDO_SMALL_CONDO_QUESTION = {
  question:
    "Does the condominium's declaration say that Wisconsin's small condominium rules (Wis. Stat. § 703.365) apply? This is possible only for condominiums with 12 or fewer units. If you're not sure, answer \"No.\"",
  optionYes: 'Yes, the declaration adopts the small condominium rules',
  optionNo: "No, or I'm not sure",
};

/** Seller delivery attestation (`WI.condo_docs.seller.attestation[.version]`). */
export const SELLER_ATTESTATION_VERSION = 'wi-condo-docs-seller-2026-09-29.L3';
export const SELLER_ATTESTATION_TEXT =
  'I am delivering the condominium disclosure materials for this unit required by Wis. Stat. § 703.33(1), with a cover sheet and index. Each document I marked "Not applicable" does not exist for this condominium, for the reason in my note. To the best of my knowledge, these are complete and current copies, as provided by the condominium association or taken from its records. I understand that the buyer may rely on these documents and may cancel the contract within 5 business days after receiving all of them (Wis. Stat. § 703.33(4)). I agree to deliver these documents electronically, and I intend the name I type below to be my signature.';

/** Supplement answering a missing-documents report (`WI.condo_docs.seller.attestation.supplement[.version]`). */
export const SELLER_ATTESTATION_SUPPLEMENT_VERSION = 'wi-condo-docs-seller-supplement-2026-10-16';
export const SELLER_ATTESTATION_SUPPLEMENT_TEXT =
  `I am delivering the documents the buyer reported missing from the condominium disclosure materials for this unit (Wis. Stat. § 703.33(3c)). Each of these documents I marked "Not applicable" does not exist for this condominium, for the reason in my note. To the best of my knowledge, these are complete and current copies, as provided by the condominium association or taken from its records. I understand that the buyer may rely on these documents, and that this delivery may affect when the buyer's time to cancel the contract ends (Wis. Stat. § 703.33(4)(b)). I agree to deliver these documents electronically, and I intend the name I type below to be my signature.`;

/** Amendment (`WI.condo_docs.seller.attestation.amendment[.version]`). */
export const SELLER_ATTESTATION_AMENDMENT_VERSION = 'wi-condo-docs-seller-amendment-2026-10-16';
export const SELLER_ATTESTATION_AMENDMENT_TEXT =
  `I am delivering an amendment to the condominium disclosure materials I delivered for this unit: the changed documents uploaded above. To the best of my knowledge, these are complete and current copies, as provided by the condominium association or taken from its records, and the documents I didn't include in this amendment have not changed since I delivered them. I understand that a change that would materially affect the buyer's rights requires the buyer's approval, and that delivering this amendment is not that approval (Wis. Stat. § 703.33(3m)). I understand that the buyer may rely on these documents and may cancel the contract within 5 business days after receiving this amendment (Wis. Stat. § 703.33(4)(a)). I agree to deliver these documents electronically, and I intend the name I type below to be my signature.`;

/**
 * The attestation a package of each kind signs, as fallbacks for the
 * summary's `attestations.seller_delivery_by_kind` (QA 2026-10-16: every kind
 * used the full-package text).
 */
export const SELLER_ATTESTATIONS_BY_KIND = {
  initial: { version: SELLER_ATTESTATION_VERSION, text: SELLER_ATTESTATION_TEXT },
  supplement: { version: SELLER_ATTESTATION_SUPPLEMENT_VERSION, text: SELLER_ATTESTATION_SUPPLEMENT_TEXT },
  amendment: { version: SELLER_ATTESTATION_AMENDMENT_VERSION, text: SELLER_ATTESTATION_AMENDMENT_TEXT },
};

/** Buyer receipt (`WI.condo_docs.buyer.receipt.{version,all,missing}`). */
export const BUYER_RECEIPT_ATTESTATION_VERSION = 'wi-condo-docs-buyer-receipt-2026-09-29.L3';
export const BUYER_RECEIPT_ALL_TEXT =
  'I confirm that I have received the condominium documents listed above and can open each one. I agree that they were delivered to me electronically through cutthe6.com, and I intend the name I type below to be my signature.';
export const BUYER_RECEIPT_PARTIAL_TEXT =
  'I received the documents listed above, but the documents I selected are missing. I am asking the seller, in writing, to deliver the missing documents (Wis. Stat. § 703.33(3c)). I intend the name I type below to be my signature.';

/**
 * Server-computed warnings by code (`WI.condo_docs.warning.*`). Each takes
 * formatted dates; the server `message` is the fallback when a date is unknown.
 */
export const CONDO_WARNING_COPY = {
  condo_docs_due_soon: ({ closingDate, deliverByDate }) =>
    `The condominium documents must be delivered to the buyer at least 15 days before closing (Wis. Stat. § 703.33(1)). With closing scheduled for ${closingDate}, deliver them by ${deliverByDate}.`,
  condo_docs_late: ({ closingDate }) =>
    `The condominium documents were not delivered at least 15 days before the scheduled closing date of ${closingDate} (Wis. Stat. § 703.33(1)). The parties may want to discuss the closing date. For help with this sale, consider talking with a Wisconsin real estate attorney.`,
  condo_review_after_closing: ({ closingDate }) =>
    `The buyer's review period is calculated to end on or after the scheduled closing date of ${closingDate}.`,
  condo_receipt_unconfirmed: ({ deliveredDate }) =>
    `The buyer hasn't confirmed receipt yet. The review period is counted from delivery on ${deliveredDate}.`,
  // The same warning while the review period waits on the buyer's first
  // access or acknowledgement (clock `not_started`): the server's
  // WARNING_RECEIPT_UNCONFIRMED_ACK_TEXT, the first sentence of the key above.
  // Saying "counted from delivery" there contradicted the clock card.
  condo_receipt_unconfirmed_not_started: () => "The buyer hasn't confirmed receipt yet.",
  // WI.condo_docs.warning.condo_awaiting_missing_documents (L4, §10)
  condo_awaiting_missing_documents: ({ requestDate, cureDate }) =>
    `The buyer asked for missing documents on ${requestDate}. The seller's deadline to deliver them is the end of the day on ${cureDate} (Wis. Stat. § 703.33(3c)). The buyer's review period will be calculated from the earlier of when the buyer receives them or that deadline (Wis. Stat. § 703.33(4)(b)).`,
  // WI.condo_docs.warning.condo_missing_request_late (L4, §10)
  condo_missing_request_late: ({ requestDate, requestByDate }) =>
    `The buyer reported missing documents on ${requestDate}, after the request deadline we calculated (${requestByDate}). The report was sent to the seller, but the review period we calculated hasn't changed. The effect of a late request under Wis. Stat. § 703.33(3c) and (4) may be disputed. For help with this sale, consider talking with a Wisconsin real estate attorney.`,
};

/** Which placeholders each warning needs, so a missing date falls back to the server text. */
export const CONDO_WARNING_NEEDS = {
  condo_docs_due_soon: ['closingDate', 'deliverByDate'],
  condo_docs_late: ['closingDate'],
  condo_review_after_closing: ['closingDate'],
  condo_receipt_unconfirmed: ['deliveredDate'],
  condo_awaiting_missing_documents: ['requestDate', 'cureDate'],
  condo_missing_request_late: ['requestDate', 'requestByDate'],
};

const TZ = CONDO_TIME_ZONE_LABEL;

export const CONDO_DOCUMENTS_COPY = {
  // Panel frame
  panelTitle: 'Condominium documents',
  statuteRef: 'Wis. Stat. § 703.33',
  loading: 'Loading condominium documents…',
  loadError: "We couldn't load the condominium documents.",
  retry: 'Try again',
  dismiss: 'Dismiss',
  warningsLabel: 'Condominium document warnings',

  // WI.condo_docs.not_applicable — the panel renders nothing on a 404, so this
  // is kept for any caller that wants to say so explicitly.
  notApplicable: "Condominium documents don't apply to this transaction.",

  // Seller: intro and checklist
  // WI.condo_docs.seller.intro
  sellerIntro:
    'Wisconsin law requires you to give the buyer the condominium disclosure documents listed below no later than 15 days before closing (Wis. Stat. § 703.33(1)). You can request them from your condominium association, which must provide them within 10 days of your written request, for a fee limited by law (Wis. Stat. § 703.33(2m)).',
  sellerInstructions:
    'Upload each document the condominium association gives you, or mark it not applicable with a short note. When everything is covered, sign the statement below and deliver the documents to the buyer.',
  smallCondoChecklistNote: 'Checklist for a small condominium (Wis. Stat. § 703.365).',
  savingSmallCondo: 'Updating the checklist…',
  startingPackage: 'Preparing your document checklist…',
  startPackage: 'Start the document checklist',
  checklistLabel: 'Condominium document checklist',
  progress: ({ done, total }) => `${done} of ${total} documents ready`,
  required: 'Required',
  optional: 'Optional',
  // Amendment rows (plain UI labels, not legal copy): only changed documents go
  // in an amendment, so an empty row isn't "Needed" (QA 2026-10-16).
  ifChanged: 'If changed',
  itemNoChangeUploaded: 'No change uploaded',
  itemReady: 'Ready',
  itemNotApplicable: 'Not applicable',
  itemPending: 'Needed',
  uploadButton: 'Upload',
  uploadAnother: 'Add another file',
  uploading: 'Uploading…',
  uploadLabel: ({ label }) => `Upload a file for ${label}`,
  removeFile: ({ name }) => `Remove ${name}`,
  markNotApplicable: 'Not applicable',
  notApplicableNoteLabel: ({ label }) => `Why ${label} doesn't apply`,
  notApplicableNotePlaceholder: 'For example: the association has no leases.',
  notApplicableNoteRequired: 'Add a short note explaining why this document does not apply.',
  saveNotApplicable: 'Save',
  undoNotApplicable: 'Undo',
  notApplicableSaved: ({ note }) => `Not applicable: ${note}`,

  // Seller: attestation and delivery
  attestationHeading: 'Confirm and deliver',
  attestationCheckbox: 'I confirm the statement above.',
  signatureLabel: 'Type your full name to sign',
  deliverButton: 'Deliver to buyer',
  delivering: 'Delivering…',
  deliverBlocked: ({ remaining }) =>
    `Upload or mark not applicable ${remaining} more ${remaining === 1 ? 'document' : 'documents'} before delivering.`,
  deliverIncomplete: 'Some documents still need an upload or a not-applicable note:',
  amendmentNeedsFile: 'Upload at least one amended document before delivering.',

  // Seller: after delivery
  // WI.condo_docs.seller.delivered (also the buyer's package header)
  delivered: ({ deliveredDate }) => `Delivered ${deliveredDate}.`,
  // WI.condo_docs.seller.buyer_confirmed
  sellerBuyerConfirmed: ({ receivedDate }) => `The buyer confirmed receipt on ${receivedDate}.`,
  // WI.condo_docs.seller.missing_request
  sellerMissingRequest: ({ requestDate, cureDate }) =>
    `The buyer reported missing documents on ${requestDate}. Wisconsin law gives you 5 business days after receiving the request to deliver them: by the end of the day on ${cureDate} (Wis. Stat. § 703.33(3c)).`,
  // UI fallback while the cure date is unknown (pending calendar).
  sellerMissingRequestNoDate: ({ requestDate }) =>
    `The buyer reported missing documents on ${requestDate}.`,
  // UI status once a delivered supplement answers the request (QA 2026-10-16:
  // the request still read as open). Factual only; no legal effect stated.
  sellerMissingAnswered: ({ requestDate, sequence, deliveredDate }) =>
    `The buyer reported missing documents on ${requestDate}. You delivered them in package #${sequence} on ${deliveredDate}.`,
  // WI.condo_docs.seller.late_request (L4, §10): a report made after the
  // § 703.33(3c) request period (`buyer_receipt.request_timely === false`).
  sellerLateRequest: ({ requestDate }) =>
    `The buyer reported missing documents on ${requestDate}, after the request deadline we calculated. We haven't changed the buyer's review period. Wisconsin law still requires you to furnish all of the documents at least 15 days before closing (Wis. Stat. § 703.33(1)), and the buyer may claim a new review period once they have all of them. Consider talking with a Wisconsin real estate attorney.`,
  missingItemsLabel: 'Documents the buyer reported missing',
  // WI.condo_docs.seller.amendment_help
  sellerAmendmentHelp:
    "If any of these documents change after delivery in a way that would materially affect the buyer's rights, Wisconsin law requires the buyer's approval first (Wis. Stat. § 703.33(3m)). Delivering an amendment starts a new 5-business-day review period for the buyer.",
  createSupplement: 'Deliver the missing documents',
  createAmendment: 'Deliver an amendment',
  packageKind: {
    initial: 'Document package',
    supplement: 'Missing documents',
    amendment: 'Amendment',
  },
  packageHeading: ({ kind, sequence }) => `${kind} #${sequence}`,
  showDocuments: 'Show documents',
  fileTypeHint: 'PDF, JPG or PNG, up to 25 MB each.',
  fileTooLarge: ({ name }) => `${name} is larger than 25 MB. Upload a smaller file.`,

  // Buyer: documents and acknowledgement
  // WI.condo_docs.buyer.intro
  buyerIntro:
    'Wisconsin law requires the seller to give you the condominium\'s disclosure documents at least 15 days before closing (Wis. Stat. § 703.33). After you receive all of them, you have 5 business days to review them and, if you choose, cancel the contract in writing without giving a reason.',
  buyerWaiting:
    "The seller hasn't delivered the condominium documents yet. You'll be notified when they arrive.",
  deliveredDocumentsLabel: 'Delivered condominium documents',
  download: ({ name }) => `Download ${name}`,
  // WI.condo_docs.buyer.ack.prompt
  receiptHeading: 'Please confirm what you received.',
  // WI.condo_docs.buyer.ack.option.all / .missing
  receiptAll: 'I received all documents listed',
  receiptMissing: 'Some documents are missing',
  // WI.condo_docs.buyer.ack.missing_help
  buyerMissingHelp: ({ requestByDate }) =>
    `You have 5 business days after receiving the documents to ask for missing ones in writing (Wis. Stat. § 703.33(3c)): by the end of the day on ${requestByDate}. Submitting this form sends your written request to the seller.`,
  // The last sentence of WI.condo_docs.buyer.ack.missing_help, alone, when the
  // request-by date is unknown.
  buyerMissingHelpNoDate: 'Submitting this form sends your written request to the seller.',
  missingPickerLegend: 'Which documents are missing?',
  missingPickerRequired: 'Select at least one missing document.',
  confirmReceiptButton: 'Confirm receipt',
  reportMissingButton: 'Report missing documents',
  submittingReceipt: 'Sending…',
  buyerYouReportedMissing: ({ date }) =>
    `You reported missing documents on ${date}. The seller has been asked to deliver them.`,
  buyerCureDeadline: ({ date }) => `The seller should deliver them by ${date}.`,
  // WI.condo_docs.buyer.late_request (L4, §10): the buyer's report came after
  // the request-by date, so the review period was not changed.
  buyerLateRequest: ({ requestDate, requestByDate }) =>
    `Your report of missing documents was sent to the seller on ${requestDate}. It was made after the ${requestByDate} request deadline we calculated (Wis. Stat. § 703.33(3c)), so we haven't changed your review period. For help with your rights, consider talking with a Wisconsin real estate attorney.`,
  buyerConfirmedReceipt: ({ date }) => `You confirmed receipt on ${date}.`,

  // Clock (both views)
  sellerDeadlineHeading: "Buyer's review period",
  buyerDeadlineHeading: 'Your review period',
  actByLabel: ({ date }) => `Act by ${date}`,
  endsLabel: ({ date }) => `Ends ${date}`,
  // WI.condo_docs.seller.clock
  sellerClock: ({ date, timeZoneLabel = TZ }) =>
    `The buyer's review period is calculated to end at the end of the day on ${date} (${timeZoneLabel || TZ}). Until then, the buyer may cancel the contract in writing without giving a reason (Wis. Stat. § 703.33(4)).`,
  // WI.condo_docs.seller.clock_hedge (revised in L4: the latest reading covers
  // the holiday question only, not a later receipt date)
  sellerClockHedge: ({ deliveredDate, latestDate }) =>
    `If a holiday in this period doesn't count as a business day, the period could end as late as ${latestDate}. If the buyer is found to have received the documents after ${deliveredDate}, it could end later. Consider talking with a Wisconsin real estate attorney before treating the period as over.`,
  // WI.condo_docs.seller.clock_hedge_ack (L4, §10): the window was counted
  // from the buyer's acknowledgement, so only the holiday question is open.
  // WI.condo_docs.seller.clock_hedge.no_holiday (added 2026-10-16): the latest
  // reading is the calculated end date, so only the receipt-date risk is left.
  sellerClockHedgeNoHoliday: ({ deliveredDate }) =>
    `If the buyer is found to have received the documents after ${deliveredDate}, the period could end later than calculated. Consider talking with a Wisconsin real estate attorney before treating the period as over.`,
  // WI.condo_docs.seller.clock_hedge_first_view.no_holiday (added 2026-10-16).
  sellerClockHedgeFirstViewNoHoliday: ({ firstViewDate }) =>
    `If the buyer is found to have received all of the documents after ${firstViewDate}, for example on the day the buyer confirmed receipt, the period could end later than calculated. Consider talking with a Wisconsin real estate attorney before treating the period as over.`,
  sellerClockHedgeAck: ({ latestDate }) =>
    `If a holiday in this period doesn't count as a business day, the period could end as late as ${latestDate}. Consider talking with a Wisconsin real estate attorney before treating the period as over.`,
  // WI.condo_docs.seller.clock_from_first_view (L4, §10): what the seller's
  // window was counted from when delivery couldn't be proven.
  sellerClockFromFirstView: ({ firstViewDate }) =>
    `We couldn't confirm electronic delivery to the buyer, so we counted the buyer's review period from ${firstViewDate}, the day the buyer first opened or downloaded a document from this package. The buyer may argue that the documents were received on a later day.`,
  // WI.condo_docs.seller.clock_hedge_first_view (L4, §10)
  sellerClockHedgeFirstView: ({ latestDate, firstViewDate }) =>
    `If a holiday in this period doesn't count as a business day, the period could end as late as ${latestDate}. If the buyer is found to have received all of the documents after ${firstViewDate}, for example on the day the buyer confirmed receipt, it could end later. Consider talking with a Wisconsin real estate attorney before treating the period as over.`,
  // WI.condo_docs.buyer.clock
  buyerClock: ({ date, deliveredDate, timeZoneLabel = TZ }) =>
    `Your review period under Wis. Stat. § 703.33(4) is calculated to end at the end of the day on ${date} (${timeZoneLabel || TZ}). We counted 5 business days, not counting weekends or Wisconsin legal holidays, starting the day after the documents were delivered to your account on ${deliveredDate}. If you decide to cancel, do it in writing before the period ends, and don't wait until the last day. A buyer who cancels on time is entitled to the return of any deposits (Wis. Stat. § 703.33(4)(c)). cutthe6.com doesn't send cancellation notices for you.`,
  // WI.condo_docs.buyer.clock_from_ack (L4-reviewed). Used for a window the
  // server counted from the buyer's acknowledgement
  // (`deadline.anchor_source = buyer_acknowledgement`: E-SIGN consent or the
  // email notice wasn't confirmed, legal §5.3).
  buyerClockFromAcknowledgement: ({ date, receivedDate, timeZoneLabel = TZ }) =>
    `Your review period under Wis. Stat. § 703.33(4) is calculated to end at the end of the day on ${date} (${timeZoneLabel || TZ}). We counted 5 business days, not counting weekends or Wisconsin legal holidays, starting the day after you confirmed receipt on ${receivedDate}. If you actually received the documents earlier, for example by opening or downloading them before you confirmed, your period may have started earlier and may end sooner. If you decide to cancel, do it in writing before the period ends, and don't wait until the last day. A buyer who cancels on time is entitled to the return of any deposits (Wis. Stat. § 703.33(4)(c)). cutthe6.com doesn't send cancellation notices for you.`,
  // WI.condo_docs.buyer.clock_from_first_view (L4, §10). A window the server
  // counted from the buyer's first download (`deadline.anchor_source =
  // buyer_first_view`): delivery couldn't be proven.
  buyerClockFromFirstView: ({ date, firstViewDate, timeZoneLabel = TZ }) =>
    `Your review period under Wis. Stat. § 703.33(4) is calculated to end at the end of the day on ${date} (${timeZoneLabel || TZ}). We counted 5 business days, not counting weekends or Wisconsin legal holidays, starting the day after you first opened or downloaded a document from this package on ${firstViewDate}. That is the earliest date our records show you had the documents. If you actually received them earlier, your period may have started earlier and may end sooner. If you decide to cancel, do it in writing before the period ends, and don't wait until the last day. A buyer who cancels on time is entitled to the return of any deposits (Wis. Stat. § 703.33(4)(c)). cutthe6.com doesn't send cancellation notices for you.`,
  // WI.condo_docs.buyer.clock_after_cure
  buyerClockAfterCure: ({ date, timeZoneLabel = TZ }) =>
    `Your review period is calculated to end at the end of the day on ${date} (${timeZoneLabel || TZ}): 5 business days after the earlier of when you received the missing documents or the seller's deadline to deliver them (Wis. Stat. § 703.33(4)(b)).`,
  // WI.condo_docs.buyer.clock_amendment
  buyerClockAmendment: ({ deliveredDate, date, timeZoneLabel = TZ }) =>
    `The seller delivered a change to the condominium documents on ${deliveredDate}. You have a separate review period for this change, calculated to end at the end of the day on ${date} (${timeZoneLabel || TZ}) (Wis. Stat. § 703.33(4)).`,
  // WI.condo_docs.buyer.clock_ambiguity
  buyerClockAmbiguity: ({ ambiguousDate, earliestDate }) =>
    `This period includes ${ambiguousDate}, an election day that Wisconsin law lists as a legal holiday. If that day were counted as a business day, the period would end on ${earliestDate}. To be safe, act by ${earliestDate}.`,
  // The last sentence of WI.condo_docs.buyer.clock_ambiguity, alone, when the
  // server gives an earlier act-by date without naming the day that causes it.
  buyerActBy: ({ earliestDate }) => `To be safe, act by ${earliestDate}.`,
  // WI.condo_docs.buyer.clock_ended
  buyerClockEnded: ({ date }) =>
    `The review period we calculated ended on ${date}. If you believe you still have a right to cancel, consider talking with a Wisconsin real estate attorney.`,
  // WI.condo_docs.clock.pending_calendar
  clockPendingCalendar:
    "We can't calculate this deadline right now. Please contact support. The law counts 5 business days, not counting weekends or Wisconsin legal holidays, starting the day after the documents are received.",
  // WI.condo_docs.clock.not_started_delivery. Only true for
  // `not_started_reason = not_delivered` (L4 §9.4); never for a package that
  // was delivered.
  clockNotStartedDelivery:
    "The review period hasn't started in cutthe6.com because the documents haven't been delivered to the buyer's account yet.",
  // WI.condo_docs.clock.awaiting_missing (L4, §10): `not_started_reason =
  // awaiting_missing_documents`, a timely missing-documents request is open.
  clockAwaitingMissing:
    "The buyer asked for missing documents. The review period will be calculated from the earlier of when the buyer receives them or the seller's deadline to deliver them (Wis. Stat. § 703.33(4)(b)).",
  // WI.condo_docs.clock.counts_from_first_access.{buyer,seller} (L4, §10).
  // `not_started_reason = delivery_unconfirmed`: the first download or the
  // acknowledgement, whichever comes first, will start the count.
  clockCountsFromFirstAccessBuyer:
    'cutthe6.com will count the review period from the day you first open or download a document from this package, or the day you confirm receipt below, whichever comes first. If you received the documents some other way before then, the legal period may already have started, so confirm what you received as soon as you can.',
  clockCountsFromFirstAccessSeller:
    "cutthe6.com couldn't confirm electronic delivery to the buyer. It will count the review period from the day the buyer first opens or downloads a document from this package, or confirms receipt, whichever comes first. If the buyer does neither, consider talking with a Wisconsin real estate attorney before relying on any deadline.",
  // WI.condo_docs.clock.counts_from_ack.{buyer,seller} (L4-reviewed). Only for
  // a rule whose anchor is the acknowledgement alone (`not_started_reason =
  // awaiting_acknowledgement`). Each says the legal period may run from an
  // earlier receipt.
  clockCountsFromAckBuyer:
    'cutthe6.com will count the review period from the day you confirm receipt below. If you received the documents earlier, for example by opening them, the legal period may already have started, so confirm what you received as soon as you can.',
  clockCountsFromAckSeller:
    "cutthe6.com will count the review period from the day the buyer confirms receipt. If the buyer received the documents earlier, the legal period may have started earlier. If the buyer doesn't confirm receipt, consider talking with a Wisconsin real estate attorney before relying on any deadline.",

  // Disclaimer (both views)
  disclaimer:
    'This is general information, not legal advice. Deadlines are calculated by cutthe6.com from the dates recorded here. For advice about your rights, consult a Wisconsin real estate attorney.',
};
